import { useEffect } from 'react'
import type { MutableRefObject } from 'react'
import * as Y from 'yjs'
import { appendHistory, makeHistoryEntry } from './history-log'

const DEBOUNCE_MS = 2500
const MIN_DELTA = 3 // coalesce trivial fixes (a char or two)

/**
 * Records the local user's manual editing bursts into the room's modification history
 * (ydoc Y.Array('history')) — CRDT-only, no server round-trip. We log ONLY local human
 * edits (`transaction.local === true`): remote peers, the AI apply (logged separately)
 * and the IndexedDB/provider initial sync all arrive as non-local and are skipped, so
 * each client logs only its own bursts with no duplication.
 *
 * `suppressRef`, when true, marks an in-flight programmatic AI apply (the "improve with
 * AI" flow) so its local writes are NOT logged as human edits — that flow records its
 * own 'ai' entry. The observer fires AFTER the mutation, so the pre-burst snapshot must
 * come from the `lastCommitted` cache, never from reading the live ytext in the observer.
 */
export function useHistoryCapture(
  ydoc: Y.Doc | null,
  ytext: Y.Text | null,
  actor: string,
  suppressRef?: MutableRefObject<boolean>,
): void {
  useEffect(() => {
    if (!ydoc || !ytext) return
    let lastCommitted = ytext.toString()
    let timer: ReturnType<typeof setTimeout> | null = null

    const flush = (): void => {
      timer = null
      const current = ytext.toString()
      const entry = makeHistoryEntry({ actor, kind: 'human', before: lastCommitted, after: current })
      lastCommitted = current
      if (!entry) return
      if (entry.before.length + entry.after.length < MIN_DELTA) return
      appendHistory(ydoc, entry)
    }

    const observer = (event: Y.YTextEvent): void => {
      if (event.transaction.local) {
        // An AI apply (improve-with-AI) writes locally too; skip it — it logs its own entry.
        if (suppressRef?.current) {
          lastCommitted = ytext.toString()
          return
        }
        if (timer) clearTimeout(timer)
        timer = setTimeout(flush, DEBOUNCE_MS)
      } else if (!timer) {
        // Remote/AI edit with no human burst in flight: rebase the baseline so the next
        // human diff is relative to the merged state (and we don't attribute it to a human).
        lastCommitted = ytext.toString()
      }
    }
    ytext.observe(observer)

    return () => {
      ytext.unobserve(observer)
      if (timer) {
        clearTimeout(timer)
        flush() // commit the in-flight burst before unmount / room change
      }
    }
  }, [ydoc, ytext, actor, suppressRef])
}
